import React, { memo, useEffect, useState } from 'react';
import {
  Typography,
  Box,
  Grid,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery
} from '@mui/material';
import Link from 'next/link';
import dynamic from 'next/dynamic';
import type { TabProperties } from '../TabsWeb';
import GeneralDrawerWrapper from '../../layouts/GeneralDrawerWrapper';
import LenderCard from '../../components/LenderCard';
import CustomMortgageSection from '../../layouts/CustomMortgageSection';
import FoxtonsLoader from '../../components/FoxtonsLoader';
import { getMortgageLenders } from '../../utils/httpRequest';
import { getMortgageData } from '../../utils';
import styles from './styles';

const Calculator = dynamic(() => import('./calculator'));

export interface LenderSelectionProps {
  propertyPrice: number;
  open: boolean;
  setOpen: () => boolean;
}
export interface TabPanelProps {
  children: React.ReactNode;
  index: string;
  value: string;
}

function LenderSelectionDrawer({
  propertyPrice,
  open = false,
  setOpen
}: LenderSelectionProps) {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [activeTab, setActiveTab] = useState('Remortgage');
  const [deals, setDeals] = useState(null);
  const [selectedLender, setSelectedLender] = useState(null);
  const [selectedInterest, setSelectedInterest] = useState(null);
  const [selectedDuration, setSelectedDuration] = useState(25);
  const [price, setPrice] = useState(null);
  const [deposit, setDeposit] = useState(propertyPrice * 0.15);
  const [interestRate, setInterestRate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isOverPayment, setOverPayment] = useState(false);
  const [overPay, setOverPay] = useState({
    pay: '0',
    payEachMonth: '0',
    atYear: '2'
  });
  const drawerRef = React.useRef(null);

  const getLenders = async (variant: string) => {
    setLoading(true);
    const mortgageType = variant === 'Buy-to-let' ? 'Buy_To_Let' : 'Standard';
    const expectedValuation = '500000';
    const numberOfItems = '10';
    const term = '25';
    const paymentMethod =
      variant === 'Buy-to-let' ? 'Interest_Only' : 'Repayment';
    const fixed = 'No_Filter';
    const overpaymentsAllowed = 'Include';
    const reasonForMortgage =
      variant === 'Remortgage' ? 'Remortgage' : 'Purchase';
    const applicantType =
      variant === 'First-time-buyers' ? 'First_Time_Buyer' : '';

    const response = await getMortgageLenders(
      mortgageType,
      expectedValuation,
      numberOfItems,
      term,
      paymentMethod,
      fixed,
      overpaymentsAllowed,
      reasonForMortgage,
      applicantType
    );

    setDeals(response?.response?.data);
    setLoading(false);
  };

  const tabsComponent = [
    {
      label: 'First time buyers',
      value: 'First-time-buyers',
      childComp: (
        <Box sx={styles.tabChildren}>
          <LenderCard deals={deals} onClick={(e) => setSelectedLender(e)} />
        </Box>
      )
    },
    {
      label: 'Remortgage',
      value: 'Remortgage',
      childComp: (
        <Box sx={styles.tabChildren}>
          <LenderCard deals={deals} onClick={(e) => setSelectedLender(e)} />
        </Box>
      )
    },
    {
      label: 'Buy-to-let',
      value: 'Buy-to-let',
      childComp: (
        <Box sx={styles.tabChildren}>
          <LenderCard deals={deals} onClick={(e) => setSelectedLender(e)} />
        </Box>
      )
    },
    {
      label: '%',
      value: '%',
      childComp: (
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          <CustomMortgageSection
            onClick={(e) => {
              setInterestRate(e);
              setSelectedInterest(e);
            }}
          />
        </Box>
      )
    }
  ];

  // eslint-disable-next-line
  function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
      <Box
        role="tabpanel"
        hidden={value !== index}
        id={`simple-tabpanels-${index}`}
        aria-labelledby={`simple-tab-${index}`}
        sx={{ flex: 1, overflow: 'auto', paddingTop: '1.5rem' }}
        {...other}
      >
        {value === index && (
          <Box>
            <Box>{children}</Box>
          </Box>
        )}
      </Box>
    );
  }
  // eslint-disable-next-line
  const LenderSelect = () => {
    const handleTabChange = (event, newValue) => {
      if (newValue !== '%') {
        setDeals(null);
        getLenders(newValue);
      }

      setActiveTab(newValue);
    };

    return (
      <Grid container xs={12} height="100%">
        <Grid item height="100%">
          <Typography variant="subtitle2" sx={styles.mainText}>
            Select a mortgage to set your repayments across
            {isSmallScreen ? (
              <Link
                prefetch={false}
                href="tel:08000 38 37 36"
                style={styles.phoneLink}
              >
                <span> Foxtons.co.uk:</span>
              </Link>
            ) : (
              <span> Foxtons.co.uk:</span>
            )}
          </Typography>
          <Box sx={styles.boxPadding}>
            <Typography variant="subtitle2" sx={styles.subText}>
              Show mortgages for:
            </Typography>
            <Box sx={styles.tabContainer}>
              <Tabs value={activeTab} onChange={handleTabChange}>
                {tabsComponent?.map((item) => (
                  <Tab
                    key={item?.value}
                    label={item?.label}
                    value={item?.value}
                    sx={
                      activeTab === item?.value
                        ? styles.selectedTabPill
                        : styles.tabPill
                    }
                  />
                ))}
              </Tabs>
              <Box sx={styles.tabBox}>
                {loading ? (
                  <Box sx={styles.loadingBox}>
                    <FoxtonsLoader open={loading} hasBackdrop={false} />
                  </Box>
                ) : (
                  tabsComponent.map(
                    ({ value, childComp }: TabProperties) =>
                      activeTab === `${value}` && (
                        <TabPanel
                          value={value}
                          index={value}
                          key={value}
                          sx={styles.tabPanel}
                        >
                          {childComp}
                        </TabPanel>
                      )
                  )
                )}
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>
    );
  };

  const getTitle = () => {
    if (selectedLender || selectedInterest) {
      const title = isSmallScreen ? (
        <>
          Alexander hall -&nbsp;
          <Link
            prefetch={false}
            href="tel:08000 38 37 36"
            style={styles.phoneLink}
          >
            <span>08000 38 37 36</span>
          </Link>
        </>
      ) : (
        'Alexander hall - 08000 38 37 36'
      );

      return title;
    }

    return `Alexander hall - today's best mortgage deals`;
  };

  useEffect(() => {
    if (selectedLender) {
      setInterestRate(selectedLender?.initialPayRate);
      setDeposit(
        (propertyPrice * (100 - (selectedLender?.maxLTVAvailable || 0))) / 100
      );
    }
  }, [selectedLender]);

  useEffect(() => {
    if (propertyPrice) {
      setPrice(propertyPrice);
    }
  }, [propertyPrice]);

  useEffect(() => {
    if (open) {
      getLenders(activeTab);
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      drawerRef?.current?.handleDrawerOpen();
    }
  }, [open]);

  useEffect(() => {
    const mortgageData = getMortgageData();

    if (mortgageData) {
      setInterestRate(mortgageData?.mortgage_rate);
      setDeposit(mortgageData?.mortgage_deposit);
      setOverPay({
        pay: mortgageData?.mortgage_lumpsum1 || 0,
        payEachMonth: mortgageData?.mortgage_overpay || 0,
        atYear: mortgageData?.mortgage_lumpsum1year || 2
      });
      setSelectedDuration(mortgageData?.mortgage_years);
    }
  }, []);

  return (
    <GeneralDrawerWrapper
      title={getTitle()}
      isOpen={open}
      handleClose={() => {
        setOpen(false);
      }}
    >
      {selectedLender || selectedInterest || getMortgageData() ? (
        <Calculator
          setOpen={setOpen}
          price={price}
          setPrice={setPrice}
          deposit={deposit}
          setDeposit={setDeposit}
          interestRate={interestRate}
          setInterestRate={setInterestRate}
          selectedDuration={selectedDuration}
          setSelectedDuration={setSelectedDuration}
          isOverPayment={isOverPayment}
          setOverPayment={setOverPayment}
          overPay={overPay}
          setOverPay={setOverPay}
          setSelectedLender={setSelectedLender}
          setSelectedInterest={setSelectedInterest}
        />
      ) : (
        <LenderSelect />
      )}
    </GeneralDrawerWrapper>
  );
}

export default memo(LenderSelectionDrawer);
